import React from "react";
import { Box, TextField, Typography } from "@mui/material";

interface InformationRequest {
  id?: string;
  question?: string;
}

interface WorkflowStepResolutionReviewProps {
  requests: InformationRequest[];
  answers: Record<string, string>;
  disabled?: boolean;
  onAnswersChange: (answers: Record<string, string>) => void;
}

interface ResolutionContent {
  step: string;
  reason: string;
  needed: string[];
}

function parseResolutionQuestion(question: string): ResolutionContent {
  const normalized = String(question || "").trim();
  const structured = normalized.match(
    /^([\s\S]*?)\n\s*\nReason:\s*\n([\s\S]*?)\n\s*\nInformation needed:\s*\n([\s\S]*?)(?:\n\s*\nProvide the information[\s\S]*)?$/i
  );
  if (structured) {
    return {
      step: structured[1].trim(),
      reason: structured[2].trim(),
      needed: structured[3]
        .split("\n")
        .map((line) => line.replace(/^\s*-\s*/, "").trim())
        .filter(Boolean),
    };
  }

  // Backward-compatible formatting for resolution requests persisted before
  // the structured question layout was introduced.
  const legacy = normalized.match(
    /^(.*?)\s+Provide the missing or corrected information[\s\S]*?\sReason:\s*([\s\S]*?)(?:\sNeeded:\s*([\s\S]*))?$/i
  );
  if (legacy) {
    return {
      step: legacy[1].trim(),
      reason: legacy[2].trim(),
      needed: legacy[3]
        ? legacy[3].split(";").map((item) => item.trim()).filter(Boolean)
        : [],
    };
  }

  return { step: "", reason: normalized, needed: [] };
}

const WorkflowStepResolutionReview: React.FC<WorkflowStepResolutionReviewProps> = ({
  requests,
  answers,
  disabled = false,
  onAnswersChange,
}) => (
  <Box>
    {requests.map((request) => {
      const id = String(request.id || "");
      const content = parseResolutionQuestion(String(request.question || ""));
      return (
        <Box key={id}>
          {content.step && (
            <Typography sx={{ color: "#172033", fontSize: 15, fontWeight: 600, mb: 1.5 }}>
              {content.step}
            </Typography>
          )}
          <Box sx={{ backgroundColor: "#FFF8F1", borderRadius: 1.5, p: 1.5, mb: 1.5 }}>
            <Typography sx={{ color: "#8A4B16", fontSize: 12, fontWeight: 700, mb: 0.5 }}>
              {content.reason.toLowerCase().startsWith("the previous attempt failed")
                ? "Why Theo is asking again"
                : "Why this step paused"}
            </Typography>
            <Typography sx={{ color: "#5F4632", fontSize: 14 }}>{content.reason}</Typography>
          </Box>
          {content.needed.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Typography sx={{ color: "#172033", fontSize: 13, fontWeight: 700, mb: 0.5 }}>
                Information needed
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, color: "#555" }}>
                {content.needed.map((item) => (
                  <li key={item}>
                    <Typography component="span" sx={{ fontSize: 14 }}>
                      {item}
                    </Typography>
                  </li>
                ))}
              </Box>
            </Box>
          )}
          <Typography sx={{ color: "#666", fontSize: 13, mb: 1 }}>
            Provide the missing information to retry, or type <strong>SKIP</strong> to omit only this step.
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={answers[id] || ""}
            onChange={(event) => onAnswersChange({ ...answers, [id]: event.target.value })}
            placeholder="Provide information or type SKIP"
            disabled={disabled}
            required
            multiline
            minRows={2}
          />
        </Box>
      );
    })}
  </Box>
);

export default WorkflowStepResolutionReview;
